import { API } from "../api";
const BASE_URL = "/cooperatives/attendance";

const attendanceExtendedApi = API.injectEndpoints({
  endpoints: (builder) => ({
    markAttendance: builder.mutation({
      query: (body) => ({
        url: `${BASE_URL}/add?guard=staff`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "AdminAttendance", id: "LIST" },
        "MemberAttendance",
        "StaffDashboard",
      ],
    }),
    getUnmarkedMembersAttendance: builder.query({
      query: ({ page, limit, date }) =>
        `${BASE_URL}/unmarked-members?guard=staff&page=${page}&limit=${limit}&date=${date}`,
      transformResponse: (responseData) => responseData?.data,
      providesTags: (result, error, arg) => {
        return [{ type: "AdminAttendance", id: "LIST" }];
      },
    }),
    getMarkedMembersAttendance: builder.query({
      query: ({ page, limit, year, month }) =>
        `${BASE_URL}/marked-members?guard=staff&page=${page}&limit=${limit}&year=${year}&month=${month}`,
      transformResponse: (responseData) => responseData?.data,
      providesTags: (result, error, arg) => {
        return [{ type: "AdminAttendance", id: "LIST" }];
      },
    }),
    getMarkedMembersByDate: builder.query({
      query: ({ date, page = 1, limit = 20 }) =>
        `${BASE_URL}/marked-members?guard=staff&date=${date}&page=${page}&limit=${limit}`,
      transformResponse: (responseData) => responseData?.data,
      providesTags: [{ type: "AdminAttendance", id: "LIST" }],
    }),
    getBranchAttendanceSummary: builder.query({
      query: () => `${BASE_URL}/summary?guard=staff`,
      transformResponse: (responseData) => responseData?.data,
      providesTags: [{ type: "AdminAttendance", id: "SUMMARY" }],
    }),
    getAttendanceByMemberId: builder.query({
      query: ({ member_id, page = 1, year, month }) => {
        // Build optional date filter in Y-m format if year/month provided
        let queryString = `${BASE_URL}/member/${member_id}?guard=staff&page=${page}`;
        if (year && month) {
          const mm = String(month).padStart(2, "0");
          queryString += `&date=${year}-${mm}`;
        }
        return queryString;
      },
      transformResponse: (responseData, meta, arg) => {
        // New backend format for member history
        const payload = responseData?.data;
        if (!payload) return { company_attendances: [], pagination: {} };

        // If backend returns old format, keep supporting it
        if (Array.isArray(payload?.company_attendances)) {
          return {
            company_attendances: payload.company_attendances,
            pagination: payload.pagination || {},
          };
        }

        // New format
        const {
          attendanceHistory = {},
          totalPresentCount,
          totalAbsentCount,
        } = payload;

        // Helper to parse a single attendance entry into a flat row
        const parseEntry = (monthKey, a) => {
          const statusId = a?.status?.id ?? a?.attendance_status_id;
          const statusName = a?.status?.name ?? a?.status_name ?? "";
          const dateLabel = a?.date || a?.attendance_date || ""; // e.g., Tue-04
          let weekDay = "";
          let dt = "";
          if (typeof dateLabel === "string" && dateLabel.includes("-")) {
            const parts = dateLabel.split("-");
            weekDay = parts[0];
            dt = parts[1];
          }
          return {
            month: monthKey,
            dateLabel,
            week_day: weekDay,
            date: dt,
            status_id: statusId,
            status_name: statusName,
          };
        };

        // Build flat entries (all months if no filter, otherwise only the selected month)
        let monthKeys = Object.keys(attendanceHistory);
        if (arg?.year && arg?.month) {
          const mm = String(arg.month).padStart(2, "0");
          const key = `${arg.year}-${mm}`;
          monthKeys = attendanceHistory[key] ? [key] : [];
        }

        const entries = [];
        let presentTotal = 0;
        let absentTotal = 0;
        monthKeys.forEach((key) => {
          const md = attendanceHistory[key] || {};
          const att = Array.isArray(md.attendance) ? md.attendance : [];
          presentTotal += md.presentCount || 0;
          absentTotal += md.absentCount || 0;
          att.forEach((a) => entries.push(parseEntry(key, a)));
        });

        // Fallback to provided totals if present
        if (typeof totalPresentCount === "number")
          presentTotal = totalPresentCount;
        if (typeof totalAbsentCount === "number")
          absentTotal = totalAbsentCount;

        // Also keep the old aggregated shape for backward compatibility (first month only)
        const company_attendances = monthKeys.slice(0, 1).map((key) => {
          const md = attendanceHistory[key] || {};
          const att = Array.isArray(md.attendance) ? md.attendance : [];
          const attendanceStatus = att.map((a) => parseEntry(key, a));
          return {
            member: {
              first_name: payload?.memberName?.split(" ")?.[0] || "",
              last_name:
                payload?.memberName?.split(" ")?.slice(1).join(" ") || "",
              photo: payload?.memberPhoto,
              email: payload?.memberEmail,
              phone: payload?.memberPhone,
            },
            month: key,
            attendanceStatus,
            attendanceCount: {
              present: md.presentCount || 0,
              absent: md.absentCount || 0,
            },
          };
        });

        // Simple pagination scaffold (client-side paging can be added later)
        const perPage = 10;
        return {
          attendance_entries: entries,
          company_attendances,
          pagination: {
            currentPage: arg?.page || 1,
            perPage,
            total: entries.length || company_attendances.length,
            lastPage: Math.max(1, Math.ceil((entries.length || 1) / perPage)),
          },
          totals: {
            totalPresentCount: presentTotal,
            totalAbsentCount: absentTotal,
          },
        };
      },
      providesTags: (result, error, arg) => {
        if (!result?.company_attendances)
          return [{ type: "AdminAttendance", id: "LIST" }];
        return [
          { type: "AdminAttendance", id: "LIST" },
          ...result.company_attendances.map(({ attendanceId }) => ({
            type: "AdminAttendance",
            id: attendanceId,
          })),
        ];
      },
    }),
    getAttendanceById: builder.query({
      query: ({ id, page = 1 }) => `${BASE_URL}/${id}/guard=staff&page=${page}`,
      transformResponse: (respnseData) => respnseData?.data,
    }),
    updateAttendanceStatus: builder.mutation({
      query: (body) => ({
        url: `${BASE_URL}/update-status?guard=staff`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { member_id }) => [
        { type: "AdminAttendance", id: "LIST" },
        { type: "AdminAttendance", id: member_id },
        "MemberAttendance",
        "StaffDashboard",
      ],
    }),
  }),
});

export const {
  useMarkAttendanceMutation,
  useGetAttendanceQuery,
  useGetAttendanceByMemberIdQuery,
  useGetAttendanceByIdQuery,
  useGetUnmarkedMembersAttendanceQuery,
  useGetMarkedMembersAttendanceQuery,
  useGetMarkedMembersByDateQuery,
  useUpdateAttendanceStatusMutation,
  useGetBranchAttendanceSummaryQuery,
} = attendanceExtendedApi;
